import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../logo.png';

const PLAY_STORE_APP_ID = 'com.retterplus.android';
const BRAND_RED = '#D50000';

/**
 * Detects whether the current device is running Android.
 * Uses a best-effort check of navigator.userAgent.
 */
function isAndroidDevice(search) {
    const params = new URLSearchParams(search);
    if (params.get('forceAndroid') === 'true') return true;
    return /android/i.test(navigator.userAgent);
}

/**
 * Builds the Play Store URLs with the encoded referrer payload.
 * market:// is the native Play Store deep link; the HTTPS URL is the fallback.
 */
function buildPlayStoreUrls(partnerCode) {
    const referrer = encodeURIComponent(`aff=${encodeURIComponent(partnerCode)}&source=affiliate`);
    const params = `id=${PLAY_STORE_APP_ID}&referrer=${referrer}`;
    return {
        marketUrl: `market://details?${params}`,
        httpsUrl: `https://play.google.com/store/apps/details?${params}`,
    };
}

const AffiliatePage = () => {
    const { partnerCode } = useParams();
    const location = useLocation();
    const { t } = useLanguage();
    const [urls, setUrls] = useState(null);

    useEffect(() => {
        if (!isAndroidDevice(location.search)) {
            // Non-Android: redirect immediately to home page
            window.location.replace('/');
            return;
        }

        // Android: build URLs
        const { marketUrl, httpsUrl } = buildPlayStoreUrls(partnerCode);
        setUrls({ marketUrl, httpsUrl });
    }, [partnerCode, location.search]);

    // Nothing to show for non-Android (redirect is in progress)
    if (!urls) return null;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700&family=Open+Sans:wght@400;600&display=swap');

                * { box-sizing: border-box; margin: 0; padding: 0; }

                body {
                    font-family: 'Open Sans', sans-serif;
                    -webkit-font-smoothing: antialiased;
                }

                .affiliate-root {
                    min-height: 100vh;
                    /* dvh accounts for mobile browser chrome (address bar) */
                    min-height: 100dvh;
                    background: linear-gradient(135deg, #0f0f0f 0%, #1a0000 50%, #0f0f0f 100%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    /* Base padding + notch / home-indicator safe areas */
                    padding: 2rem;
                    padding-bottom: max(2rem, env(safe-area-inset-bottom));
                    padding-left:  max(2rem, env(safe-area-inset-left));
                    padding-right: max(2rem, env(safe-area-inset-right));
                }

                .affiliate-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    padding: 3rem 2.5rem;
                    /* fluid width: fills narrow screens, caps on wide ones */
                    width: min(100%, 480px);
                    text-align: center;
                    box-shadow:
                        0 25px 60px rgba(0, 0, 0, 0.5),
                        0 0 0 1px rgba(213, 0, 0, 0.15),
                        inset 0 1px 0 rgba(255,255,255,0.08);
                }

                .affiliate-logo {
                    width: 72px;
                    height: 72px;
                    object-fit: contain;
                    margin-bottom: 1.5rem;
                    filter: drop-shadow(0 4px 16px rgba(213, 0, 0, 0.4));
                }

                @keyframes affiliate-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.6; transform: scale(0.95); }
                }

                .affiliate-title {
                    font-family: 'Montserrat', sans-serif;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #ffffff;
                    margin-bottom: 0.875rem;
                    line-height: 1.3;
                    letter-spacing: -0.01em;
                    /* Prevent long words / German compounds from overflowing */
                    overflow-wrap: break-word;
                    word-break: break-word;
                    hyphens: auto;
                }

                .affiliate-subtitle {
                    font-size: 1rem;
                    color: rgba(255, 255, 255, 0.65);
                    line-height: 1.6;
                    margin-bottom: 2.25rem;
                    overflow-wrap: break-word;
                    word-break: break-word;
                    hyphens: auto;
                }

                .affiliate-divider {
                    width: 40px;
                    height: 2px;
                    background: linear-gradient(90deg, transparent, ${BRAND_RED}, transparent);
                    margin: 0 auto 2.25rem;
                }

                .affiliate-fallback-link {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    background: linear-gradient(135deg, #D50000, #b71c1c);
                    color: #ffffff;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.95rem;
                    padding: 0.875rem 1.75rem;
                    border-radius: 50px;
                    transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
                    box-shadow: 0 6px 24px rgba(213, 0, 0, 0.35);
                    animation: affiliate-pulse 2.5s ease-in-out infinite;
                    /* Fill card width so long translated strings don't overflow */
                    width: 100%;
                    overflow-wrap: break-word;
                    word-break: break-word;
                    hyphens: auto;
                    text-align: center;
                }

                .affiliate-fallback-link:hover,
                .affiliate-fallback-link:focus {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 32px rgba(213, 0, 0, 0.5);
                    opacity: 0.95;
                    animation: none;
                }

                .affiliate-fallback-link:active {
                    transform: translateY(0);
                }

                .affiliate-chooser-instruction {
                    font-size: 0.85rem;
                    color: rgba(255, 255, 255, 0.5);
                    margin-top: 1.5rem;
                    line-height: 1.4;
                    overflow-wrap: break-word;
                    word-break: break-word;
                    hyphens: auto;
                }

                .affiliate-play-icon {
                    width: 20px;
                    height: 20px;
                    flex-shrink: 0;
                }

                /* ── Very small phones (≤ 360px) ─────────────────────────────── */
                @media (max-width: 360px) {
                    .affiliate-root {
                        padding: 1rem 0.75rem;
                        padding-bottom: max(1rem, env(safe-area-inset-bottom));
                        padding-left:   max(0.75rem, env(safe-area-inset-left));
                        padding-right:  max(0.75rem, env(safe-area-inset-right));
                    }
                    .affiliate-card {
                        padding: 1.75rem 1.1rem;
                        border-radius: 16px;
                    }
                    .affiliate-logo {
                        width: 52px;
                        height: 52px;
                        margin-bottom: 1rem;
                    }
                    .affiliate-title {
                        font-size: 1.1rem;
                        margin-bottom: 0.65rem;
                    }
                    .affiliate-subtitle {
                        font-size: 0.875rem;
                        margin-bottom: 1.75rem;
                    }
                    .affiliate-divider  { margin-bottom: 1.75rem; }
                    .affiliate-fallback-link {
                        font-size: 0.85rem;
                        padding: 0.75rem 1rem;
                    }
                    .affiliate-play-icon {
                        width: 16px;
                        height: 16px;
                    }
                }

                /* ── Small phones (361px – 480px) ────────────────────────────── */
                @media (min-width: 361px) and (max-width: 480px) {
                    .affiliate-root {
                        padding: 1.25rem 1rem;
                        padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
                        padding-left:   max(1rem, env(safe-area-inset-left));
                        padding-right:  max(1rem, env(safe-area-inset-right));
                    }
                    .affiliate-card {
                        padding: 2.25rem 1.5rem;
                        border-radius: 20px;
                    }
                    .affiliate-logo {
                        width: 60px;
                        height: 60px;
                        margin-bottom: 1.25rem;
                    }
                    .affiliate-title   { font-size: 1.25rem; }
                    .affiliate-subtitle { font-size: 0.9375rem; }
                    .affiliate-fallback-link {
                        font-size: 0.9rem;
                        padding: 0.8rem 1.4rem;
                    }
                }

                /* ── Mid phones / phablets (481px – 767px) ───────────────────── */
                @media (min-width: 481px) and (max-width: 767px) {
                    .affiliate-root { padding: 2rem 1.5rem; }
                    .affiliate-card { padding: 2.75rem 2rem; }
                    .affiliate-title { font-size: 1.4rem; }
                }

                /* ── Tablets (768px – 1023px) ────────────────────────────────── */
                @media (min-width: 768px) and (max-width: 1023px) {
                    .affiliate-card {
                        width: min(100%, 520px);
                        padding: 3.25rem 3rem;
                        border-radius: 28px;
                    }
                    .affiliate-logo  { width: 84px; height: 84px; }
                    .affiliate-title { font-size: 1.75rem; }
                    .affiliate-subtitle { font-size: 1.0625rem; }
                    .affiliate-fallback-link {
                        font-size: 1rem;
                        padding: 1rem 2rem;
                    }
                }

                /* ── Desktop (≥ 1024px) ──────────────────────────────────────── */
                @media (min-width: 1024px) {
                    .affiliate-card {
                        width: min(100%, 540px);
                        padding: 3.5rem 3.25rem;
                        border-radius: 32px;
                    }
                    .affiliate-logo  { width: 88px; height: 88px; }
                    .affiliate-title { font-size: 1.875rem; }
                    .affiliate-subtitle { font-size: 1.0625rem; }
                    .affiliate-fallback-link {
                        font-size: 1rem;
                        padding: 1rem 2.25rem;
                    }
                }

                /* ── Landscape on short screens (e.g. phone rotated) ─────────── */
                @media (max-height: 500px) and (orientation: landscape) {
                    .affiliate-root {
                        justify-content: flex-start;
                        overflow-y: auto;
                        padding: 1rem 2rem;
                    }
                    .affiliate-card {
                        padding: 1.5rem 2rem;
                        margin: 1rem auto;
                    }
                    .affiliate-logo {
                        width: 44px;
                        height: 44px;
                        margin-bottom: 0.75rem;
                    }
                    .affiliate-title {
                        font-size: 1.1rem;
                        margin-bottom: 0.5rem;
                    }
                    .affiliate-subtitle {
                        font-size: 0.875rem;
                        margin-bottom: 1.25rem;
                    }
                    .affiliate-divider { margin-bottom: 1.25rem; }
                }
            `}</style>

            <div className="affiliate-root">
                <div className="affiliate-card">
                    <img
                        src={logo}
                        alt="RetterPlus"
                        className="affiliate-logo"
                    />

                    <h1 className="affiliate-title">
                        {t('affiliate.welcomeTitle')}
                    </h1>

                    <p className="affiliate-subtitle">
                        {t('affiliate.installInfo')}
                    </p>

                    <div className="affiliate-divider" />

                    <a
                        href={urls.marketUrl}
                        className="affiliate-fallback-link"
                        id="affiliate-fallback-cta"
                        rel="noopener noreferrer"
                    >
                        {/* Google Play triangle icon (inline SVG) */}
                        <svg
                            className="affiliate-play-icon"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l15 8.5c.5.29.5 1.21 0 1.5L4.5 21c-.5.33-1.5.33-1.5-.5z" />
                        </svg>
                        {t('affiliate.fallbackText')}
                    </a>

                    <p className="affiliate-chooser-instruction">
                        {t('affiliate.chooserInstruction')}
                    </p>
                </div>
            </div>
        </>
    );
};

export default AffiliatePage;
